import type {
  AccountValidationRules,
  PaymentType,
  ProductType,
} from '@/services';
import { proposalsService } from '@/services/proposals';
import type { PaymentInfo, Proposal } from '@/services/proposals/types';
import { RequiredFieldTooltip } from '@/shared/components/required-field-tooltip';
import { WarningNotice } from '@/shared/ui';
import { toaster } from '@/shared/ui/toaster.component';
import { formatCurrency } from '@/shared/utils';
import type { BankMaskConfig } from '@/shared/utils/bank-account.helper';
import {
  resolveBankKey,
  resolveBankMaskConfig,
  validateBankAccountDigit,
  validateBankAgencyDigit,
} from '@/shared/utils/bank-account.helper';
import {
  Button,
  Calendar,
  Checkbox,
  Field,
  Select,
  Text,
} from '@noorden-insurtech/alba-design-system';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { DateTime } from 'luxon';
import type { HTMLAttributes } from 'react';
import { useEffect, useMemo, useState } from 'react';
import {
  PremiumCard,
  type InstallmentOption,
} from '../../../quotes/components/premium-card.component';
import {
  useInstallmentTypes,
  usePaymentTypes,
  usePixAutomaticAccount,
} from '../../hooks';
import { PixBankCombobox } from './pix-bank-combobox.component';
import { getInstallmentOptions } from '../../utils/installment-options.util';

type StepPaymentProps = {
  onBack: () => void;
  onNext: () => void;
  isViewMode?: boolean;
  allowDifferentPayment?: boolean;
  /**
   * `CodForma`s que o combo deve oferecer. Vazio = sem filtro (fluxo
   * tradicional); preenchido = produto que cobra pelo gateway, e aí aparecem
   * as formas do link ligadas e as legadas.
   */
  allowedPaymentMethodCodes?: string[];
  /** Quais `CodForma`s são cobrados por link; os outros seguem o I4Pro. */
  gatewayPaymentMethodCodes?: string[];
  /**
   * `CodForma`s fora do combo, com ou sem gateway: formas do gateway que o
   * link não cobra agora (e que o I4Pro também não cobra).
   */
  hiddenPaymentMethodCodes?: string[];
  /**
   * Produto que cobra por link: quantos dias antes de cada vencimento o link da
   * parcela chega ao segurado. Ausente no fluxo tradicional — e a dica some,
   * como some para uma forma legada.
   */
  chargeLeadDays?: number;
  /**
   * Produto que cobra o PIX parcelado por PIX Automático: com PIX em mais de uma
   * parcela, o step pede a conta do segurado (o mandato exige banco e conta).
   */
  pixAutomatic?: boolean;
  /**
   * Produto que cobra o cartão parcelado numa transação só: a apólice é emitida
   * em parcela única e o segurado parcela no cartão, num link só.
   */
  cardSingleTransaction?: boolean;
  /**
   * Produto cujo catálogo de formas de pagamento o combo deve mostrar.
   * OBRIGATÓRIO de propósito: enquanto tinha default `HOME_INSURANCE`, a
   * proposta empresarial oferecia o combo do residencial — inclusive PIX e
   * cartão, que o 180007 não tem.
   */
  productType: ProductType;
  policyId?: string;
  effectiveDate?: string;
  terminateDate?: string;
  proposal: Proposal;
  isBusinessProposal?: boolean;
};

type PaymentState = {
  paymentMethod: string;
  installment: string;
  agency: string;
  agencyDigit: string;
  account: string;
  accountDigit: string;
  differentPayment: boolean;
  otherPaymentMethod: string;
  otherAgency: string;
  otherAgencyDigit: string;
  otherAccount: string;
  otherAccountDigit: string;
  firstDueDate: string;
  differentDueDate: boolean;
  otherDueDate: string;
};

type PaymentErrors = Partial<Record<keyof PaymentState, string>>;

const defaultState: PaymentState = {
  paymentMethod: '',
  installment: '',
  agency: '',
  agencyDigit: '',
  account: '',
  accountDigit: '',
  differentPayment: false,
  otherPaymentMethod: '',
  otherAgency: '',
  otherAgencyDigit: '',
  otherAccount: '',
  otherAccountDigit: '',
  firstDueDate: '',
  differentDueDate: false,
  otherDueDate: '',
};

/**
 * Formas em que a apólice inteira é cobrada do mesmo jeito (cartão, PIX e Boleto
 * PIX, cobrados por link ou mandato): "meio diferente nas demais parcelas" não
 * se aplica, então o checkbox nem aparece. Valores da coluna `BillingMethod`.
 */
const SINGLE_METHOD_BILLING_METHODS = new Set([
  'CARTAO_CREDITO',
  'PIX',
  'BOLETO_PIX',
]);

/**
 * O PIX Automático debita todo mês no MESMO dia da 1ª mensalidade, a partir de
 * hoje + 2. A 1ª parcela em outro dia (ou antes disso) vai por link, e o mandato
 * cobre as demais — a decisão final é da emissão, com as datas do I4Pro.
 */
const PIX_AUTOMATIC_MIN_LEAD_DAYS = 2;

function pixAutomaticFirstInstallmentApart(
  state: Pick<PaymentState, 'differentDueDate' | 'firstDueDate'>
): boolean {
  if (state.differentDueDate) return true;
  if (!state.firstDueDate) return false;
  const first = DateTime.fromISO(state.firstDueDate.split('T')[0]);
  const minimum = DateTime.now()
    .startOf('day')
    .plus({ days: PIX_AUTOMATIC_MIN_LEAD_DAYS });
  return first.isValid && first < minimum;
}

/** Teto de vezes no cartão da Finance.API. */
const MAX_CARD_INSTALLMENTS = 12;

/**
 * Cartão de crédito: todas as parcelas vencem no mesmo dia — "vencimento
 * diferente nas demais parcelas" não se aplica.
 */
function isCardBilling(
  paymentTypes: PaymentType[] | undefined,
  paymentMethodCode: string
): boolean {
  return (
    paymentTypes?.find(pt => pt.code === paymentMethodCode)?.billingMethod ===
    'CARTAO_CREDITO'
  );
}

function isSingleMethodBilling(
  paymentTypes: PaymentType[] | undefined,
  paymentMethodCode: string
): boolean {
  const billingMethod = paymentTypes?.find(
    pt => pt.code === paymentMethodCode
  )?.billingMethod;
  return (
    billingMethod != null && SINGLE_METHOD_BILLING_METHODS.has(billingMethod)
  );
}

const digitsOnlyFieldMaxLength: Partial<Record<keyof PaymentState, number>> = {
  agency: 5,
  account: 13,
  otherAgency: 5,
  otherAccount: 13,
  otherDueDate: 2,
};

const digitFieldMaxLength: Partial<Record<keyof PaymentState, number>> = {
  agencyDigit: 1,
  accountDigit: 2,
  otherAgencyDigit: 1,
  otherAccountDigit: 2,
};

function sanitizeDigitsInput(value: string, maxLength?: number) {
  const digits = String(value || '').replace(/\D/g, '');
  if (typeof maxLength === 'number') return digits.slice(0, maxLength);
  return digits;
}

// Máscaras de conta por banco e validação de DV — centralizadas em
// @/shared/utils/bank-account.helper

function sanitizeDigitInput(value: string, maxLength?: number) {
  const alphanumeric = String(value || '')
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, '');
  if (typeof maxLength === 'number') return alphanumeric.slice(0, maxLength);
  return alphanumeric;
}

function isDigits(value: string) {
  return /^\d+$/.test(value);
}

function validateAccountWithRules(
  rules: AccountValidationRules | undefined,
  agencyNumber: string,
  agencyDigit: string,
  accountNumber: string,
  accountDigit: string
): {
  agencyNumberValid: boolean;
  agencyValid: boolean;
  accountNumberValid: boolean;
  accountValid: boolean;
} {
  if (!rules)
    return {
      agencyNumberValid: true,
      agencyValid: true,
      accountNumberValid: true,
      accountValid: true,
    };

  const agencyNumberMatchesPattern = /^[0-9]{4,5}$/.test(agencyNumber);
  const agencyNumberIsOnlyZeros = /^0{4,5}$/.test(agencyNumber);
  const accountNumberMatchesPattern = /^[0-9]{5,13}$/.test(accountNumber);
  const accountNumberIsOnlyZeros = /^0{5,13}$/.test(accountNumber);

  return {
    agencyNumberValid: agencyNumberMatchesPattern && !agencyNumberIsOnlyZeros,
    agencyValid: new RegExp(rules.agencyDigitPattern, 'i').test(agencyDigit),
    accountNumberValid:
      accountNumberMatchesPattern && !accountNumberIsOnlyZeros,
    accountValid: new RegExp(rules.accountDigitPattern, 'i').test(accountDigit),
  };
}

const digitFields = new Set<keyof PaymentState>([
  'agencyDigit',
  'accountDigit',
  'otherAgencyDigit',
  'otherAccountDigit',
]);

function toInstallmentDateOnly(isoDate: string | undefined | null): string {
  if (!isoDate) return '';
  const s = String(isoDate);
  return s.split('T')[0]?.split(' ')[0] ?? '';
}

function dayOfMonthFromInstallmentDate(isoDate: string): number | null {
  const d = toInstallmentDateOnly(isoDate);
  const day = Number(d.split('-')[2]);
  return Number.isFinite(day) ? day : null;
}

/**
 * Várias linhas com o mesmo PaymentMethod = uma entrada por opção de parcela do produto (InsureMO),
 * não configuração de "meio diferente nas demais parcelas".
 */
function isInsureMoInstallmentOptionCatalog(blocks: PaymentInfo[]): boolean {
  if (!blocks || blocks.length < 2) return false;
  const methods = blocks
    .map(b => String(b.paymentMethod ?? '').trim())
    .filter(Boolean);
  if (methods.length < blocks.length) return false;
  return new Set(methods).size === 1;
}

/**
 * Texto do PremiumCard: no catálogo (várias linhas, mesmo paymentMethod — ex.: 1x…10x com 1000013),
 * usa o bloco com maior installmentPeriodCount; com um único bloco “real”, usa esse ou o último.
 */
export function getPremiumInstallmentSummary(proposal: Proposal): {
  installmentCount: number;
  installmentValue: number;
} {
  const list = proposal.paymentInfo;
  if (!list?.length) {
    return { installmentCount: 0, installmentValue: 0 };
  }

  if (isInsureMoInstallmentOptionCatalog(list)) {
    const best = list.reduce((acc, cur) => {
      const curCount =
        cur.installmentPeriodCount ?? cur.installmentList?.length ?? 0;
      const accCount =
        acc.installmentPeriodCount ?? acc.installmentList?.length ?? 0;
      return curCount >= accCount ? cur : acc;
    });
    const installments = best.installmentList ?? [];
    return {
      installmentCount: best.installmentPeriodCount ?? installments.length,
      installmentValue: installments[0]?.installmentAmount ?? 0,
    };
  }

  const last = list[list.length - 1];
  const installments = last?.installmentList ?? [];
  return {
    installmentCount: last?.installmentPeriodCount ?? installments.length,
    installmentValue: installments[0]?.installmentAmount ?? 0,
  };
}

/**
 * No catálogo InsureMO (várias linhas, mesmo meio), só hidrata se a policy já refletir
 * a opção escolhida (InstallPeriod na policy alinhado a um bloco).
 */
function selectCatalogBlockIfPolicyInstallPeriodMatches(
  blocks: PaymentInfo[],
  proposal: Proposal
): PaymentInfo | null {
  const raw = proposal.installPeriod;
  if (raw === undefined || raw === null) return null;
  const target = Number(raw);
  if (!Number.isFinite(target)) return null;
  return blocks.find(b => Number(b.installPeriod) === target) ?? null;
}

function parseBankNumberWithDigit(
  value: string | number | undefined,
  maxNumberLength: number,
  maxDigitLength: number
) {
  let number = '';
  let digit = '';
  const raw = value == null ? '' : String(value).trim();

  if (raw) {
    const dash = raw.indexOf('-');
    if (dash >= 0) {
      number = raw.slice(0, dash).replace(/\D/g, '');
      digit = raw
        .slice(dash + 1)
        .toUpperCase()
        .replace(/[^0-9A-Z]/g, '');
    } else {
      number = raw.replace(/\D/g, '');
    }
  }

  return {
    number: number.slice(0, maxNumberLength),
    digit: digit.slice(0, maxDigitLength),
  };
}

function getHydratedInstallmentCount(
  firstBlock: PaymentInfo,
  effectiveBlocks: PaymentInfo[]
) {
  if (effectiveBlocks.length >= 2) {
    const total = effectiveBlocks.reduce((sum, block) => {
      const count =
        block.installmentPeriodCount > 0
          ? block.installmentPeriodCount
          : block.installmentList?.length || 0;
      return sum + count;
    }, 0);

    if (total > 0) return total;
  }

  const installments = firstBlock.installmentList;
  return firstBlock.installmentPeriodCount > 0
    ? firstBlock.installmentPeriodCount
    : installments.length;
}

function applyDebitFieldsFromPaymentBlock(
  block: PaymentInfo,
  target: PaymentState,
  prefix: 'primary' | 'other'
) {
  const agencyParts = parseBankNumberWithDigit(block.agencyNo, 5, 1);
  const accountParts = parseBankNumberWithDigit(block.accountNo, 13, 2);

  const legacyAgencyParts = !agencyParts.number
    ? parseBankNumberWithDigit(block.accountHolderIdNo, 5, 1)
    : null;
  const legacyAgencyDigit =
    legacyAgencyParts && block.accountHolderIdType
      ? String(block.accountHolderIdType)
          .toUpperCase()
          .replace(/[^0-9A-Z]/g, '')
          .slice(0, 1)
      : legacyAgencyParts?.digit;

  if (prefix === 'primary') {
    target.agency = agencyParts.number || legacyAgencyParts?.number || '';
    target.agencyDigit = agencyParts.digit || legacyAgencyDigit || '';
    target.account = accountParts.number;
    target.accountDigit = accountParts.digit;
  } else {
    target.otherAgency = agencyParts.number || legacyAgencyParts?.number || '';
    target.otherAgencyDigit = agencyParts.digit || legacyAgencyDigit || '';
    target.otherAccount = accountParts.number;
    target.otherAccountDigit = accountParts.digit;
  }
}

/**
 * Monta o estado do formulário de pagamento a partir do retorno da API (PolicyPaymentInfoList).
 * O primeiro bloco costuma concentrar parcelas e meio principal; o segundo, meio alternativo.
 */
export function buildPaymentStateFromProposal(
  proposal: Proposal
): PaymentState | null {
  const blocks = proposal.paymentInfo;
  if (!blocks?.length) return null;

  let effectiveBlocks = blocks;
  if (isInsureMoInstallmentOptionCatalog(blocks)) {
    const selected = selectCatalogBlockIfPolicyInstallPeriodMatches(
      blocks,
      proposal
    );
    if (!selected) return null;
    effectiveBlocks = [selected];
  }

  const firstBlock = effectiveBlocks[0];
  const installments = firstBlock.installmentList;
  if (!installments?.length) return null;

  const next: PaymentState = { ...defaultState };

  const periodCount = getHydratedInstallmentCount(firstBlock, effectiveBlocks);
  next.installment = String(periodCount);
  next.paymentMethod = String(firstBlock.paymentMethod ?? '');

  next.firstDueDate = toInstallmentDateOnly(installments[0].installmentDate);

  if (installments.length >= 2) {
    const d0 = dayOfMonthFromInstallmentDate(installments[0].installmentDate);
    const d1 = dayOfMonthFromInstallmentDate(installments[1].installmentDate);
    if (d0 !== null && d1 !== null && d0 !== d1) {
      next.differentDueDate = true;
      next.otherDueDate = String(d1);
    }
  }

  applyDebitFieldsFromPaymentBlock(firstBlock, next, 'primary');

  if (effectiveBlocks.length >= 2) {
    const secondBlock = effectiveBlocks[1];
    next.differentPayment = true;
    next.otherPaymentMethod = String(secondBlock.paymentMethod ?? '');
    applyDebitFieldsFromPaymentBlock(secondBlock, next, 'other');
  }

  return next;
}

function isDigitField(key: keyof PaymentState) {
  return digitFields.has(key);
}

function computeDigitError(
  key: keyof PaymentState,
  value: string,
  state: PaymentState,
  paymentTypesData: PaymentType[] | undefined
): string | undefined {
  if (!value) return undefined;

  const isOther = (key as string).startsWith('other');
  const paymentCode = isOther ? state.otherPaymentMethod : state.paymentMethod;
  const paymentType = paymentTypesData?.find(pt => pt.code === paymentCode);
  const rules = paymentType?.accountValidationRules;
  const isAgencyDigit = key === 'agencyDigit' || key === 'otherAgencyDigit';

  // Validação do DV por banco para os bancos suportados no helper
  // (bank-account.helper.ts). Tanto para agência quanto para conta usamos
  // algoritmos específicos por banco quando disponíveis, com fallback para
  // Módulo 11 genérico. Para DV da conta o algoritmo depende da conta já
  // estar preenchida; para DV da agência, depende da agência preenchida.
  const bankKey = resolveBankKey(paymentType?.description);
  if (bankKey) {
    const agency = isOther ? state.otherAgency : state.agency;

    if (isAgencyDigit) {
      if (agency) {
        const isValid = validateBankAgencyDigit(bankKey, agency, value);
        if (isValid === false) {
          return 'Dígito da agência inválido para o banco selecionado.';
        }
      }
      return undefined;
    }

    const account = isOther ? state.otherAccount : state.account;
    if (account) {
      const isValid = validateBankAccountDigit(bankKey, agency, account, value);
      if (isValid === false) {
        return 'Dígito da conta inválido para o banco selecionado.';
      }
    }
    return undefined;
  }

  if (!rules) return undefined;

  const pattern = isAgencyDigit
    ? rules.agencyDigitPattern
    : rules.accountDigitPattern;
  const isValid = new RegExp(pattern, 'i').test(value);

  return isValid
    ? undefined
    : isAgencyDigit
      ? 'Dígito da agência inválido para o banco selecionado.'
      : 'Dígito da conta inválido para o banco selecionado.';
}

export function StepPayment({
  onBack,
  onNext,
  isViewMode = false,
  allowDifferentPayment = true,
  allowedPaymentMethodCodes = [],
  gatewayPaymentMethodCodes = [],
  hiddenPaymentMethodCodes = [],
  chargeLeadDays,
  pixAutomatic = false,
  cardSingleTransaction = false,
  productType,
  policyId,
  effectiveDate,
  terminateDate,
  proposal,
  isBusinessProposal = false,
}: StepPaymentProps) {
  const [state, setState] = useState<PaymentState>(defaultState);
  const [errors, setErrors] = useState<PaymentErrors>({});
  const [pixAccount, setPixAccount] =
    useState<PixAccountState>(emptyPixAccount);
  const [pixAccountErrors, setPixAccountErrors] = useState<PixAccountErrors>(
    {}
  );

  // Conta já gravada (proposta reaberta): preenche uma vez, sem atropelar o que
  // o corretor estiver digitando.
  const { data: savedPixAccount } = usePixAutomaticAccount(
    policyId,
    pixAutomatic
  );
  useEffect(() => {
    if (!savedPixAccount) return;
    setPixAccount(current =>
      current.ispb
        ? current
        : {
            ispb: savedPixAccount.ispb,
            bankName: savedPixAccount.bankName,
            branchNumber: savedPixAccount.branchNumber ?? '',
            accountNumber: savedPixAccount.accountNumber,
          }
    );
  }, [savedPixAccount]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const paymentHydrateSignature = useMemo(() => {
    const list = proposal.paymentInfo;
    const first = list?.[0];
    const last = list?.length ? list[list.length - 1] : undefined;
    const firstDue = first?.installmentList?.[0]?.installmentDate;
    return [
      proposal.policyId,
      proposal.versionSeq ?? '',
      proposal.installPeriod ?? '',
      last?.pk ?? '',
      last?.paymentMethod ?? '',
      first?.installmentPeriodCount ?? '',
      first?.installPeriod ?? '',
      firstDue ?? '',
      list?.length ?? 0,
    ].join('|');
  }, [proposal]);

  useEffect(() => {
    const hydrated = buildPaymentStateFromProposal(proposal);
    if (!hydrated) {
      setState(defaultState);
      setErrors({});
      setHasSubmitted(false);
      return;
    }
    setState(hydrated);
    setErrors({});
    setHasSubmitted(false);
  }, [paymentHydrateSignature]);

  const { data: rawPaymentTypesData } = usePaymentTypes(productType);

  /**
   * O combo oferece só o que alguém cobra: saem as formas do gateway que o link
   * não cobra agora (desligadas no produto, ou o gateway inteiro), e as legadas
   * continuam. Os rótulos e códigos vêm do catálogo — a config apenas diz QUAIS
   * linhas entram, para não casar por texto. Na visualização, a forma já
   * gravada continua na lista: sem ela o campo apareceria vazio.
   */
  const paymentTypesData = useMemo(() => {
    if (!rawPaymentTypesData) return rawPaymentTypesData;
    const hidden = new Set(hiddenPaymentMethodCodes);
    const allowed =
      allowedPaymentMethodCodes.length > 0
        ? new Set(allowedPaymentMethodCodes)
        : null;
    const saved = new Set(
      isViewMode ? [state.paymentMethod, state.otherPaymentMethod] : []
    );
    return rawPaymentTypesData.filter(
      paymentType =>
        saved.has(paymentType.code) ||
        (!hidden.has(paymentType.code) &&
          (!allowed || allowed.has(paymentType.code)))
    );
  }, [
    rawPaymentTypesData,
    allowedPaymentMethodCodes,
    hiddenPaymentMethodCodes,
    isViewMode,
    state.paymentMethod,
    state.otherPaymentMethod,
  ]);
  const { data: installmentTypesData } = useInstallmentTypes(
    productType,
    isBusinessProposal
  );

  const premiumValue = proposal?.grossPremium ?? 0;

  const { installmentCount } = useMemo(
    () => getPremiumInstallmentSummary(proposal),
    [proposal]
  );

  const computedInstallmentValue =
    installmentCount > 0 ? premiumValue / installmentCount : 0;

  const premiumInstallmentOptions = useMemo(
    () => getInstallmentOptions(proposal),
    [proposal]
  );

  const paymentMethodOptions = useMemo(
    () =>
      paymentTypesData?.map(paymentType => ({
        value: paymentType.code,
        label: paymentType.description,
      })) || [],
    [paymentTypesData]
  );

  const installmentOptions = useMemo(
    () =>
      (installmentTypesData ?? [])
        .slice()
        .sort((a, b) => a.installmentPeriodCount - b.installmentPeriodCount)
        .map(installmentType => ({
          value: String(installmentType.installmentPeriodCount),
          label: installmentType.description,
        })),
    [installmentTypesData]
  );

  const firstDueDateMin = useMemo(() => {
    return DateTime.now().startOf('day').plus({ days: 12 }).toISODate() || '';
  }, []);

  const firstDueDateMax = useMemo(() => {
    return DateTime.now().startOf('day').plus({ days: 30 }).toISODate() || '';
  }, []);

  const isDebitPayment = useMemo(() => {
    const selectedPayment = paymentTypesData?.find(
      pt => pt.code === state.paymentMethod
    );
    return (
      selectedPayment?.description.toLowerCase().includes('débito') || false
    );
  }, [state.paymentMethod, paymentTypesData]);

  /**
   * PIX Automático vale para PIX parcelado em produto no modo AUTOMATIC. O
   * critério é o `billingMethod` do catálogo, nunca o texto da forma.
   */
  const isPixAutomaticActive = useMemo(() => {
    if (!pixAutomatic || Number(state.installment) <= 1) return false;
    const selected = paymentTypesData?.find(
      pt => pt.code === state.paymentMethod
    );
    return selected?.billingMethod === 'PIX';
  }, [pixAutomatic, state.installment, state.paymentMethod, paymentTypesData]);

  const canUseDifferentPayment =
    allowDifferentPayment &&
    !isSingleMethodBilling(paymentTypesData, state.paymentMethod);
  const canUseDifferentDueDate = !isCardBilling(
    paymentTypesData,
    state.paymentMethod
  );
  const isCardSingleTransactionActive =
    cardSingleTransaction &&
    Number(state.installment) > 1 &&
    isCardBilling(paymentTypesData, state.paymentMethod);
  // Forma legada num produto com gateway: o I4Pro cobra, sem link — a dica do
  // link não vale para ela.
  const linkChargeLeadDays = gatewayPaymentMethodCodes.includes(
    state.paymentMethod
  )
    ? chargeLeadDays
    : undefined;

  const isOtherDebitPayment = useMemo(() => {
    const selectedPayment = paymentTypesData?.find(
      pt => pt.code === state.otherPaymentMethod
    );
    return (
      selectedPayment?.description.toLowerCase().includes('débito') || false
    );
  }, [state.otherPaymentMethod, paymentTypesData]);

  const bankMaskConfig = useMemo(() => {
    const selected = paymentTypesData?.find(
      pt => pt.code === state.paymentMethod
    );
    return resolveBankMaskConfig(selected?.description);
  }, [state.paymentMethod, paymentTypesData]);

  const otherBankMaskConfig = useMemo(() => {
    const selected = paymentTypesData?.find(
      pt => pt.code === state.otherPaymentMethod
    );
    return resolveBankMaskConfig(selected?.description);
  }, [state.otherPaymentMethod, paymentTypesData]);

  // Limpa o dígito da agência quando o banco selecionado não utiliza este campo
  // (ex: Santander, Itaú). Evita que valores antigos fiquem "presos" ao trocar
  // de forma de pagamento entre bancos.
  useEffect(() => {
    if (
      bankMaskConfig &&
      !bankMaskConfig.agencyDigitEnabled &&
      state.agencyDigit
    ) {
      setState(prev => ({ ...prev, agencyDigit: '' }));
    }
  }, [bankMaskConfig, state.agencyDigit]);

  useEffect(() => {
    if (
      otherBankMaskConfig &&
      !otherBankMaskConfig.agencyDigitEnabled &&
      state.otherAgencyDigit
    ) {
      setState(prev => ({ ...prev, otherAgencyDigit: '' }));
    }
  }, [otherBankMaskConfig, state.otherAgencyDigit]);

  const handleChange = <K extends keyof PaymentState>(
    key: K,
    value: PaymentState[K]
  ) => {
    let nextValue = value;
    if (typeof value === 'string') {
      const fallbackDigitMax = digitFieldMaxLength[key];
      const digitsMax = digitsOnlyFieldMaxLength[key];
      const isOther = (key as string).startsWith('other');
      const activeBankMask = isOther ? otherBankMaskConfig : bankMaskConfig;
      if (typeof fallbackDigitMax === 'number') {
        const paymentCode = isOther
          ? state.otherPaymentMethod
          : state.paymentMethod;
        const rules = paymentTypesData?.find(
          pt => pt.code === paymentCode
        )?.accountValidationRules;
        const isAgencyDigit =
          key === 'agencyDigit' || key === 'otherAgencyDigit';
        const bankDigitMax = activeBankMask
          ? isAgencyDigit
            ? activeBankMask.agencyDigitLength
            : activeBankMask.accountDigitLength
          : undefined;
        const dynamicMax =
          typeof bankDigitMax === 'number'
            ? bankDigitMax
            : rules
              ? isAgencyDigit
                ? rules.agencyDigitMaxLength
                : rules.accountDigitMaxLength
              : fallbackDigitMax;
        nextValue = sanitizeDigitInput(value, dynamicMax) as PaymentState[K];
      } else if (typeof digitsMax === 'number') {
        const isAgency = key === 'agency' || key === 'otherAgency';
        const isAccount = key === 'account' || key === 'otherAccount';
        const bankBaseMax = activeBankMask
          ? isAgency
            ? activeBankMask.agencyLength
            : isAccount
              ? activeBankMask.accountLength
              : undefined
          : undefined;
        const dynamicMax =
          typeof bankBaseMax === 'number' ? bankBaseMax : digitsMax;
        nextValue = sanitizeDigitsInput(value, dynamicMax) as PaymentState[K];
      }
    }

    setState(prev => {
      const next = { ...prev, [key]: nextValue };
      if (hasSubmitted) {
        setErrors(computeErrors(next));
      } else if (typeof nextValue === 'string' && isDigitField(key)) {
        const digitError = computeDigitError(
          key,
          nextValue,
          prev,
          paymentTypesData
        );
        setErrors(prevErrors => ({ ...prevErrors, [key]: digitError }));
      } else {
        setErrors(prevErrors => ({ ...prevErrors, [key]: undefined }));
      }
      return next;
    });
  };

  const handleSelectChange = <K extends keyof PaymentState>(
    key: K,
    value: string
  ) => {
    setState(prev => {
      const next: PaymentState = {
        ...prev,
        [key]: prev[key] === value ? '' : value,
      };
      if (key === 'paymentMethod') {
        next.agencyDigit = '';
        next.accountDigit = '';
        if (isCardBilling(paymentTypesData, next.paymentMethod)) {
          next.differentDueDate = false;
          next.otherDueDate = '';
        }
        if (isSingleMethodBilling(paymentTypesData, next.paymentMethod)) {
          next.differentPayment = false;
          next.otherPaymentMethod = '';
          next.otherAgency = '';
          next.otherAgencyDigit = '';
          next.otherAccount = '';
          next.otherAccountDigit = '';
        }
      } else if (key === 'otherPaymentMethod') {
        next.otherAgencyDigit = '';
        next.otherAccountDigit = '';
      } else if (key === 'installment') {
        const newInstallment = Number(next.installment);
        if (!next.installment || newInstallment <= 1) {
          next.differentDueDate = false;
          next.otherDueDate = '';
          next.differentPayment = false;
          next.otherPaymentMethod = '';
          next.otherAgency = '';
          next.otherAgencyDigit = '';
          next.otherAccount = '';
          next.otherAccountDigit = '';
        }
      }
      if (hasSubmitted) {
        setErrors(computeErrors(next));
      }
      return next;
    });

    if (!hasSubmitted) {
      setErrors(prev => ({ ...prev, [key]: undefined }));
    }
  };

  const computeErrors = (s: PaymentState): PaymentErrors => {
    const nextErrors: PaymentErrors = {};

    if (!s.paymentMethod) {
      nextErrors.paymentMethod = 'Selecione o meio de pagamento.';
    }

    if (!s.installment) {
      nextErrors.installment = 'Selecione a opção de parcelamento.';
    }

    const currentIsDebitPayment = !!paymentTypesData
      ?.find(pt => pt.code === s.paymentMethod)
      ?.description.toLowerCase()
      .includes('débito');

    if (currentIsDebitPayment) {
      const selectedPaymentType = paymentTypesData?.find(
        pt => pt.code === s.paymentMethod
      );

      if (!s.agency) {
        nextErrors.agency = 'Informe a agência.';
      } else if (!isDigits(s.agency)) {
        nextErrors.agency = 'A agência deve conter apenas números.';
      }

      if (!s.account) {
        nextErrors.account = 'Informe a conta.';
      } else if (!isDigits(s.account)) {
        nextErrors.account = 'A conta deve conter apenas números.';
      }

      if (!s.accountDigit) {
        nextErrors.accountDigit = 'Informe o dígito da conta.';
      }

      if (s.agencyDigit && s.accountDigit) {
        const {
          agencyNumberValid,
          agencyValid,
          accountNumberValid,
          accountValid,
        } = validateAccountWithRules(
          selectedPaymentType?.accountValidationRules,
          s.agency,
          s.agencyDigit,
          s.account,
          s.accountDigit
        );
        if (!agencyNumberValid) {
          nextErrors.agency = 'A agência deve conter apenas números.';
        }
        if (!agencyValid) {
          nextErrors.agencyDigit =
            'Dígito da agência inválido para o banco selecionado.';
        }
        if (!accountNumberValid) {
          nextErrors.account = 'A conta deve conter apenas números.';
        }
        if (!accountValid) {
          nextErrors.accountDigit =
            'Dígito da conta inválido para o banco selecionado.';
        }
      }

      // Validação do DV da conta por banco (BB, Santander, Bradesco, Itaú).
      // Só executa se não houver erros prévios nos campos envolvidos.
      if (
        s.account &&
        s.accountDigit &&
        !nextErrors.account &&
        !nextErrors.accountDigit
      ) {
        const bankKey = resolveBankKey(selectedPaymentType?.description);
        if (bankKey) {
          const isValid = validateBankAccountDigit(
            bankKey,
            s.agency,
            s.account,
            s.accountDigit
          );
          if (isValid === false) {
            nextErrors.accountDigit =
              'Dígito da conta inválido para o banco selecionado.';
          }
        }
      }

      // Validação do DV da agência por banco (para bancos que exigem dígito
      // de agência: BB, Bradesco, Safra, Citibank, ABC, Banco do Nordeste,
      // Banrisul).
      if (
        s.agency &&
        s.agencyDigit &&
        !nextErrors.agency &&
        !nextErrors.agencyDigit
      ) {
        const bankKey = resolveBankKey(selectedPaymentType?.description);
        if (bankKey) {
          const isValid = validateBankAgencyDigit(
            bankKey,
            s.agency,
            s.agencyDigit
          );
          if (isValid === false) {
            nextErrors.agencyDigit =
              'Dígito da agência inválido para o banco selecionado.';
          }
        }
      }
    }

    if (
      s.differentPayment &&
      allowDifferentPayment &&
      !isSingleMethodBilling(paymentTypesData, s.paymentMethod)
    ) {
      if (!s.otherPaymentMethod) {
        nextErrors.otherPaymentMethod = 'Selecione o meio de pagamento.';
      }

      const currentIsOtherDebitPayment = !!paymentTypesData
        ?.find(pt => pt.code === s.otherPaymentMethod)
        ?.description.toLowerCase()
        .includes('débito');

      if (currentIsOtherDebitPayment) {
        const selectedOtherPaymentType = paymentTypesData?.find(
          pt => pt.code === s.otherPaymentMethod
        );

        if (!s.otherAgency) {
          nextErrors.otherAgency = 'Informe a agência.';
        } else if (!isDigits(s.otherAgency)) {
          nextErrors.otherAgency = 'A agência deve conter apenas números.';
        }

        if (!s.otherAccount) {
          nextErrors.otherAccount = 'Informe a conta.';
        } else if (!isDigits(s.otherAccount)) {
          nextErrors.otherAccount = 'A conta deve conter apenas números.';
        }

        if (!s.otherAccountDigit) {
          nextErrors.otherAccountDigit = 'Informe o dígito da conta.';
        }

        if (s.otherAgencyDigit && s.otherAccountDigit) {
          const {
            agencyNumberValid,
            agencyValid,
            accountNumberValid,
            accountValid,
          } = validateAccountWithRules(
            selectedOtherPaymentType?.accountValidationRules,
            s.otherAgency,
            s.otherAgencyDigit,
            s.otherAccount,
            s.otherAccountDigit
          );
          if (!agencyNumberValid) {
            nextErrors.otherAgency = 'A agência deve conter apenas números.';
          }
          if (!agencyValid) {
            nextErrors.otherAgencyDigit =
              'Dígito da agência inválido para o banco selecionado.';
          }
          if (!accountNumberValid) {
            nextErrors.otherAccount = 'A conta deve conter apenas números.';
          }
          if (!accountValid) {
            nextErrors.otherAccountDigit =
              'Dígito da conta inválido para o banco selecionado.';
          }
        }

        if (
          s.otherAccount &&
          s.otherAccountDigit &&
          !nextErrors.otherAccount &&
          !nextErrors.otherAccountDigit
        ) {
          const bankKey = resolveBankKey(selectedOtherPaymentType?.description);
          if (bankKey) {
            const isValid = validateBankAccountDigit(
              bankKey,
              s.otherAgency,
              s.otherAccount,
              s.otherAccountDigit
            );
            if (isValid === false) {
              nextErrors.otherAccountDigit =
                'Dígito da conta inválido para o banco selecionado.';
            }
          }
        }

        if (
          s.otherAgency &&
          s.otherAgencyDigit &&
          !nextErrors.otherAgency &&
          !nextErrors.otherAgencyDigit
        ) {
          const bankKey = resolveBankKey(selectedOtherPaymentType?.description);
          if (bankKey) {
            const isValid = validateBankAgencyDigit(
              bankKey,
              s.otherAgency,
              s.otherAgencyDigit
            );
            if (isValid === false) {
              nextErrors.otherAgencyDigit =
                'Dígito da agência inválido para o banco selecionado.';
            }
          }
        }
      }
    }

    if (!s.firstDueDate) {
      nextErrors.firstDueDate = 'Selecione a data da primeira parcela.';
    } else {
      const selectedDate = DateTime.fromISO(s.firstDueDate);
      const minDate = DateTime.fromISO(firstDueDateMin);
      const maxDate = DateTime.fromISO(firstDueDateMax);

      if (!selectedDate.isValid || !minDate.isValid || !maxDate.isValid) {
        nextErrors.firstDueDate = 'Data inválida.';
      } else if (selectedDate.weekday === 6 || selectedDate.weekday === 7) {
        nextErrors.firstDueDate =
          'A data da 1ª parcela não pode ser em fim de semana.';
      } else if (selectedDate < minDate) {
        nextErrors.firstDueDate =
          'A data da 1ª parcela deve ser de no mínimo 12 dias corridos a partir de hoje.';
      } else if (selectedDate > maxDate) {
        nextErrors.firstDueDate =
          'A data da 1ª parcela deve ser de no máximo 30 dias corridos a partir de hoje.';
      }
    }

    if (
      s.differentDueDate &&
      !isCardBilling(paymentTypesData, s.paymentMethod)
    ) {
      if (!s.otherDueDate) {
        nextErrors.otherDueDate =
          'Informe o dia de vencimento das demais parcelas.';
      } else {
        const day = Number(s.otherDueDate);
        if (!Number.isInteger(day) || day < 1 || day > 31) {
          nextErrors.otherDueDate = 'Informe um dia válido entre 1 e 31.';
        }
      }
    }

    return nextErrors;
  };

  const validate = (s: PaymentState = state): boolean => {
    const nextErrors = computeErrors(s);
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleNext = async () => {
    setHasSubmitted(true);
    const pixErrors = isPixAutomaticActive
      ? validatePixAccount(pixAccount)
      : {};
    setPixAccountErrors(pixErrors);
    const isValid = validate();
    if (!isValid || Object.keys(pixErrors).length > 0) {
      return;
    }

    if (!policyId) {
      toaster.create({
        title: 'Erro',
        description: 'ID da proposta não encontrado.',
        type: 'error',
        duration: 5000,
      });
      return;
    }

    if (!effectiveDate || !terminateDate) {
      toaster.create({
        title: 'Erro',
        description:
          'Datas de vigência não encontradas. Por favor, preencha as datas no passo anterior.',
        type: 'error',
        duration: 5000,
      });
      return;
    }

    setIsLoading(true);

    try {
      const selectedPaymentType = paymentTypesData?.find(
        pt => pt.code === state.paymentMethod
      );
      const selectedOtherPaymentType = paymentTypesData?.find(
        pt => pt.code === state.otherPaymentMethod
      );

      const selectedInstallmentType = installmentTypesData?.find(
        it => String(it.installmentPeriodCount) === state.installment
      );

      const payload = {
        effectiveDate,
        terminateDate,
        paymentInfo: {
          firstPaymentType: state.paymentMethod,
          firstInstallmentCode: Number(selectedInstallmentType?.code ?? 0),
          installmentCount: Number(state.installment),
          dueDateFirstInstallment: state.firstDueDate,
          ...(state.differentPayment &&
            canUseDifferentPayment && {
              secondPaymentType: state.otherPaymentMethod,
              secondInstallmentCode: Number(selectedInstallmentType?.code ?? 0),
              ...(isOtherDebitPayment &&
                selectedOtherPaymentType?.bankCode && {
                  secondBankCode: selectedOtherPaymentType.bankCode,
                }),
              ...(isOtherDebitPayment && {
                secondAccount: {
                  agencyNumber: state.otherAgency,
                  agencyDigit: state.otherAgencyDigit,
                  accountNumber: state.otherAccount,
                  accountDigit: state.otherAccountDigit,
                },
              }),
            }),
          ...(state.differentDueDate &&
            canUseDifferentDueDate && {
              dueDayOtherInstallments: Number(state.otherDueDate),
            }),
          ...(isPixAutomaticActive && {
            pixAutomaticAccount: {
              ispb: pixAccount.ispb,
              bankName: pixAccount.bankName,
              ...(pixAccount.branchNumber && {
                branchNumber: pixAccount.branchNumber,
              }),
              accountNumber: pixAccount.accountNumber,
            },
          }),
          ...(isDebitPayment && {
            ...(selectedPaymentType?.bankCode && {
              bankCode: selectedPaymentType.bankCode,
            }),
            firstAccount: {
              agencyNumber: state.agency,
              // Não substituir vazio por '0': alguns bancos (Santander, Itaú,
              // Caixa e vários digitais) não têm dígito de agência. Se
              // enviarmos '0' como default, o backend concatena "agencia-0" e
              // o InsureMO grava "AgencyNo: 1234-0" (bug).
              agencyDigit: state.agencyDigit,
              accountNumber: state.account,
              accountDigit: state.accountDigit,
            },
          }),
        },
      };

      const attachResult = await proposalsService.attachPaymentInfo(
        policyId,
        payload,
        proposal?.productCode
      );

      if (!attachResult.success) {
        toaster.create({
          title: 'Erro ao anexar informações de pagamento',
          description:
            attachResult.error ||
            'Não foi possível anexar as informações de pagamento. Tente novamente.',
          type: 'error',
          duration: 5000,
        });
        setIsLoading(false);
        return;
      }

      const calculateResult = await proposalsService.calculateProposal(
        policyId,
        proposal?.productCode
      );

      if (!calculateResult.success) {
        toaster.create({
          title: 'Erro ao calcular proposta',
          description:
            calculateResult.error ||
            'Não foi possível calcular a proposta. Tente novamente.',
          type: 'error',
          duration: 5000,
        });
        setIsLoading(false);
        return;
      }

      toaster.create({
        title: 'Sucesso',
        description: 'Informações de pagamento anexadas e proposta calculada.',
        type: 'success',
        duration: 3000,
      });

      onNext();
    } catch (error) {
      toaster.create({
        title: 'Erro',
        description:
          error instanceof Error
            ? error.message
            : 'Erro inesperado ao processar pagamento.',
        type: 'error',
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className='flex flex-col gap-8'>
      <PaymentOptionsCard
        premiumValue={formatCurrency(premiumValue)}
        installmentText={`Até ${installmentCount}x sem juros ${formatCurrency(computedInstallmentValue)}`}
        installmentOptions={premiumInstallmentOptions}
      />

      <PaymentInfoFields
        state={state}
        errors={errors}
        isViewMode={isViewMode}
        isDebitPayment={isDebitPayment}
        bankMaskConfig={bankMaskConfig}
        paymentMethodOptions={paymentMethodOptions}
        installmentOptions={installmentOptions}
        allowDifferentPayment={allowDifferentPayment}
        hideDifferentPayment={isSingleMethodBilling(
          paymentTypesData,
          state.paymentMethod
        )}
        chargeLeadDays={linkChargeLeadDays}
        pixAutomaticActive={isPixAutomaticActive}
        cardSingleTransactionActive={isCardSingleTransactionActive}
        onChange={handleChange}
        onSelectChange={handleSelectChange}
      />

      {isPixAutomaticActive && (
        <PixAutomaticAccountFields
          value={pixAccount}
          errors={pixAccountErrors}
          disabled={isViewMode}
          onChange={next => {
            setPixAccount(next);
            if (hasSubmitted) setPixAccountErrors(validatePixAccount(next));
          }}
        />
      )}

      {state.differentPayment && canUseDifferentPayment && (
        <PaymentDifferentPaymentFields
          state={state}
          errors={errors}
          isViewMode={isViewMode}
          isOtherDebitPayment={isOtherDebitPayment}
          bankMaskConfig={otherBankMaskConfig}
          paymentMethodOptions={paymentMethodOptions}
          onChange={handleChange}
          onSelectChange={handleSelectChange}
        />
      )}

      <PaymentDueDateFields
        state={state}
        errors={errors}
        isViewMode={isViewMode}
        allowDifferentDueDate={canUseDifferentDueDate}
        firstDueDateMin={firstDueDateMin}
        firstDueDateMax={firstDueDateMax}
        onChange={handleChange}
      />

      <PaymentActions
        onBack={onBack}
        onNext={handleNext}
        isViewMode={isViewMode}
        isLoading={isLoading}
      />
    </div>
  );
}

type FormInputProps = {
  label: string;
  placeholder?: string;
  required?: boolean;
  value: string;
  onChange: OnChange;
  error?: string;
  disabled?: boolean;
  type?: 'text' | 'date';
  inputMode?: HTMLAttributes<HTMLInputElement>['inputMode'];
  pattern?: string;
  min?: string;
  max?: string;
  maxLength?: number;
};

type PixAccountState = {
  ispb: string;
  bankName: string;
  branchNumber: string;
  accountNumber: string;
};

type PixAccountErrors = Partial<Record<keyof PixAccountState, string>>;

const emptyPixAccount: PixAccountState = {
  ispb: '',
  bankName: '',
  branchNumber: '',
  accountNumber: '',
};

/** As mesmas regras que a Finance.API confere ao criar o mandato. */
function validatePixAccount(account: PixAccountState): PixAccountErrors {
  const errors: PixAccountErrors = {};
  if (!/^\d{8}$/.test(account.ispb)) {
    errors.ispb = 'Selecione o banco do segurado.';
  }
  if (account.branchNumber && !/^\d{1,10}$/.test(account.branchNumber)) {
    errors.branchNumber = 'Use só números (até 10).';
  }
  if (!/^\d{1,20}$/.test(account.accountNumber)) {
    errors.accountNumber = 'Informe a conta com o dígito, só números.';
  }
  return errors;
}

/** Conta do segurado para o mandato do PIX Automático. */
function PixAutomaticAccountFields({
  value,
  errors,
  disabled,
  onChange,
}: {
  value: PixAccountState;
  errors: PixAccountErrors;
  disabled: boolean;
  onChange: (next: PixAccountState) => void;
}) {
  const onlyDigits = (text: string) => text.replace(/\D/g, '');

  return (
    <div className='flex flex-col gap-4'>
      <Text className='text-base font-semibold text-alba-black-500'>
        Conta do segurado para o PIX Automático
      </Text>
      <Text className='text-sm text-alba-gray-500'>
        A conta precisa ser de titularidade do segurado: é nela que ele autoriza
        o PIX Automático e de onde as parcelas são debitadas.
      </Text>
      <div className='grid grid-cols-1 gap-6 lg:grid-cols-3'>
        <PixBankCombobox
          value={
            value.ispb
              ? { ispb: value.ispb, bankName: value.bankName }
              : undefined
          }
          onChange={bank =>
            onChange({
              ...value,
              ispb: bank?.ispb ?? '',
              bankName: bank?.bankName ?? '',
            })
          }
          error={errors.ispb}
          disabled={disabled}
        />
        <FormInput
          label='Agência'
          placeholder='Somente números'
          value={value.branchNumber}
          onChange={text =>
            onChange({ ...value, branchNumber: onlyDigits(text) })
          }
          error={errors.branchNumber}
          disabled={disabled}
          inputMode='numeric'
          maxLength={10}
        />
        <FormInput
          label='Conta com dígito'
          placeholder='Somente números'
          required
          value={value.accountNumber}
          onChange={text =>
            onChange({ ...value, accountNumber: onlyDigits(text) })
          }
          error={errors.accountNumber}
          disabled={disabled}
          inputMode='numeric'
          maxLength={20}
        />
      </div>
    </div>
  );
}

function FormInput({
  label,
  placeholder,
  required,
  value,
  onChange,
  error,
  disabled,
  type = 'text',
  inputMode,
  pattern,
  min,
  max,
  maxLength,
}: FormInputProps) {
  return (
    <RequiredFieldTooltip active={required}>
      <Field.Root invalid={!!error}>
        <Field.Label>
          {label}
          {required && <span className='text-alba-error-red-500'>*</span>}
        </Field.Label>
        <Field.Input
          type={type}
          value={value}
          onChange={event => onChange(event.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          inputMode={inputMode}
          pattern={pattern}
          min={min}
          max={max}
          maxLength={maxLength}
        />
        {error && <Field.ErrorText>{error}</Field.ErrorText>}
      </Field.Root>
    </RequiredFieldTooltip>
  );
}

type FormSelectOption = {
  value: string;
  label: string;
};

type OnChange = (_value: string) => void;

type FormSelectProps = {
  label: string;
  placeholder?: string;
  required?: boolean;
  options: FormSelectOption[];
  value: string;
  onChange: OnChange;
  error?: string;
  disabled?: boolean;
};

function FormSelect({
  label,
  placeholder,
  required,
  options,
  value,
  onChange,
  error,
  disabled,
}: FormSelectProps) {
  const [open, setOpen] = useState(false);

  return (
    <RequiredFieldTooltip active={required}>
      <Field.Root invalid={!!error}>
        <Field.Label>
          {label}
          {required && <span className='text-alba-error-red-500'>*</span>}
        </Field.Label>
        <Select.Root
          value={value ? [value] : []}
          onValueChange={details => {
            const newValue = details.value[0] || '';
            if (newValue === value) {
              onChange('');
            } else {
              onChange(newValue);
            }
          }}
          open={open}
          onOpenChange={details => {
            if (disabled) return;
            const nextOpen =
              typeof details === 'boolean'
                ? details
                : // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  !!(details as any)?.open;
            setOpen(nextOpen);
          }}
          positioning={{ sameWidth: true }}
          items={options}
          disabled={disabled}
        >
          <Select.Control>
            <Select.Trigger className='w-full'>
              <Select.ValueText
                placeholder={placeholder || 'Selecione'}
                className='block w-full truncate text-left whitespace-nowrap'
              />
              <Select.Indicator />
            </Select.Trigger>
          </Select.Control>
          <Select.Positioner>
            <Select.Content>
              {options.map(option => (
                <Select.Item
                  key={option.value}
                  item={option}
                  onPointerDownCapture={event => {
                    if (disabled) return;
                    if (value && value === option.value) {
                      event.preventDefault();
                      event.stopPropagation();
                      onChange('');
                      setOpen(false);
                    }
                  }}
                >
                  <Select.ItemText>{option.label}</Select.ItemText>
                  <Select.ItemIndicator />
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Positioner>
        </Select.Root>
        {error && <Field.ErrorText>{error}</Field.ErrorText>}
      </Field.Root>
    </RequiredFieldTooltip>
  );
}

type PaymentOptionsCardProps = {
  premiumValue: string;
  installmentText: string;
  installmentOptions?: InstallmentOption[];
};

function PaymentOptionsCard({
  premiumValue,
  installmentText,
  installmentOptions,
}: PaymentOptionsCardProps) {
  return (
    <div className='flex flex-col gap-4'>
      <Text className='text-base font-semibold text-alba-black-500'>
        Opções de Parcelamento
      </Text>
      <div className='flex justify-center'>
        <div className='flex w-full max-w-[704px] flex-col items-center gap-2'>
          <PremiumCard
            value={premiumValue}
            installmentText={installmentText}
            installmentOptions={installmentOptions}
          />
        </div>
      </div>
    </div>
  );
}

type PaymentStateChangeHandler = <K extends keyof PaymentState>(
  _key: K,
  _value: PaymentState[K]
) => void;

type PaymentSelectChangeHandler = <K extends keyof PaymentState>(
  _key: K,
  _value: string
) => void;

type PaymentInfoFieldsProps = {
  state: PaymentState;
  errors: PaymentErrors;
  isViewMode: boolean;
  isDebitPayment: boolean;
  bankMaskConfig: BankMaskConfig | null;
  paymentMethodOptions: FormSelectOption[];
  installmentOptions: FormSelectOption[];
  allowDifferentPayment: boolean;
  /** Forma cobrada inteira do mesmo jeito (cartão, PIX, Boleto PIX). */
  hideDifferentPayment?: boolean;
  chargeLeadDays?: number;
  pixAutomaticActive?: boolean;
  /** Cartão parcelado numa transação só (apólice em parcela única). */
  cardSingleTransactionActive?: boolean;
  onChange: PaymentStateChangeHandler;
  onSelectChange: PaymentSelectChangeHandler;
};

function PaymentInfoFields({
  state,
  errors,
  isViewMode,
  isDebitPayment,
  bankMaskConfig,
  paymentMethodOptions,
  installmentOptions,
  allowDifferentPayment,
  hideDifferentPayment = false,
  chargeLeadDays,
  pixAutomaticActive = false,
  cardSingleTransactionActive = false,
  onChange,
  onSelectChange,
}: PaymentInfoFieldsProps) {
  const showAnotherPaymentMethod =
    state.installment && Number(state.installment) > 1;

  const agencyMaxLength = bankMaskConfig?.agencyLength ?? 5;
  const agencyPlaceholder =
    bankMaskConfig?.agencyPlaceholder ?? 'Número da agência';
  const agencyDigitEnabled = bankMaskConfig
    ? bankMaskConfig.agencyDigitEnabled
    : true;
  const agencyDigitMaxLength = bankMaskConfig?.agencyDigitLength ?? 1;
  const agencyDigitPlaceholder = bankMaskConfig
    ? bankMaskConfig.agencyDigitEnabled
      ? bankMaskConfig.agencyDigitPlaceholder
      : 'Não se aplica'
    : 'Dígito da agência';
  const accountMaxLength = bankMaskConfig?.accountLength ?? 13;
  const accountPlaceholder =
    bankMaskConfig?.accountPlaceholder ?? 'Número da conta';
  const accountDigitMaxLength = bankMaskConfig?.accountDigitLength ?? 2;
  const accountDigitPlaceholder =
    bankMaskConfig?.accountDigitPlaceholder ?? 'Dígito de controle';

  return (
    <>
      <Text className='text-base font-semibold text-alba-black-500'>
        Informações de Pagamento
      </Text>

      <div className='grid grid-cols-1 gap-6 lg:grid-cols-2'>
        <FormSelect
          label='Meio de Pagamento'
          placeholder='Selecione o meio de pagamento'
          required
          options={paymentMethodOptions}
          value={state.paymentMethod}
          onChange={value => onSelectChange('paymentMethod', value)}
          error={errors.paymentMethod}
          disabled={isViewMode}
        />
        <FormSelect
          label='Parcelamento'
          placeholder='Selecione a opção de parcelamento'
          required
          options={installmentOptions}
          value={state.installment}
          onChange={value => onSelectChange('installment', value)}
          error={errors.installment}
          disabled={isViewMode}
        />
        {/*
          Agência e conta só existem no débito em conta. Em PIX, boleto ou
          cartão não há o que preencher: campo desabilitado ocupa a tela e
          ainda sugere que falta algo. Ficam ocultos — o payload já os omite
          fora do débito, então nada muda no que é enviado.
        */}
        {isDebitPayment && (
          <>
            <FormInput
              label='Agência'
              placeholder={agencyPlaceholder}
              required
              value={state.agency}
              onChange={value => onChange('agency', value)}
              error={errors.agency}
              disabled={isViewMode}
              type='text'
              inputMode='numeric'
              pattern={`[0-9]{1,${agencyMaxLength}}`}
              maxLength={agencyMaxLength}
            />
            <FormInput
              label='Dígito da Agência'
              placeholder={agencyDigitPlaceholder}
              required={false}
              value={state.agencyDigit}
              onChange={value => onChange('agencyDigit', value)}
              error={errors.agencyDigit}
              disabled={isViewMode || !agencyDigitEnabled}
              type='text'
              maxLength={agencyDigitMaxLength}
            />
            <FormInput
              label='Conta'
              placeholder={accountPlaceholder}
              required
              value={state.account}
              onChange={value => onChange('account', value)}
              error={errors.account}
              disabled={isViewMode}
              type='text'
              inputMode='numeric'
              pattern={`[0-9]{1,${accountMaxLength}}`}
              maxLength={accountMaxLength}
            />
            <FormInput
              label='Dígito da Conta'
              placeholder={accountDigitPlaceholder}
              required
              value={state.accountDigit}
              onChange={value => onChange('accountDigit', value)}
              error={errors.accountDigit}
              disabled={isViewMode}
              type='text'
              maxLength={accountDigitMaxLength}
            />
          </>
        )}
      </div>

      {/*
        Parcelado num produto que cobra por link: o segurado não recebe tudo de
        uma vez. Vale dizer ao corretor, que é quem explica a cobrança ao cliente.
      */}
      {pixAutomaticActive ? (
        <>
          <Text className='text-sm text-alba-gray-500'>
            PIX Automático: o segurado autoriza uma vez no app do banco e as
            parcelas são debitadas todo mês, no vencimento.
            {pixAutomaticFirstInstallmentApart(state) &&
              (Number(state.installment) - 1 >= 2
                ? ' Como a 1ª parcela não cai no mesmo dia das demais (ou vence em menos de 2 dias), ela vai por link de PIX e o PIX Automático cobra as parcelas seguintes.'
                : ' Como a 1ª parcela não cai no mesmo dia das demais (ou vence em menos de 2 dias), sobraria uma parcela só para o PIX Automático: todas vão por link de PIX.')}
          </Text>
          <WarningNotice>
            <strong>Prazo para autorizar o PIX Automático:</strong> o segurado
            tem até <strong>30 dias</strong>, contados da emissão da apólice,
            para autorizar no app do banco. Sem a autorização nesse prazo, o PIX
            Automático expira e as parcelas precisam ser cobradas por link de
            pagamento.
          </WarningNotice>
        </>
      ) : cardSingleTransactionActive ? (
        <Text className='text-sm text-alba-gray-500'>
          Cartão de crédito: o segurado paga num link só, parcelado em até{' '}
          {Math.min(Number(state.installment), MAX_CARD_INSTALLMENTS)}x no
          cartão, e a apólice é emitida em parcela única.
          {chargeLeadDays != null &&
            ` O link é enviado por e-mail ${chargeLeadDays} dias antes do vencimento.`}
        </Text>
      ) : (
        chargeLeadDays != null &&
        showAnotherPaymentMethod && (
          <Text className='text-sm text-alba-gray-500'>
            Cada parcela é cobrada por um link de pagamento enviado ao segurado
            por e-mail {chargeLeadDays} dias antes do vencimento.
          </Text>
        )
      )}

      {showAnotherPaymentMethod && !hideDifferentPayment && (
        <div className='flex flex-col gap-4'>
          <Checkbox.Root
            checked={state.differentPayment}
            disabled={isViewMode || !allowDifferentPayment}
            onCheckedChange={details => {
              if (isViewMode || !allowDifferentPayment) return;
              const raw =
                typeof details === 'boolean' || typeof details === 'string'
                  ? details
                  : details?.checked;
              const checked = raw === 'indeterminate' ? false : !!raw;
              onChange('differentPayment', checked);
            }}
            onClick={() => {
              if (isViewMode || !allowDifferentPayment) return;
              const current = state.differentPayment || false;
              onChange('differentPayment', !current);
            }}
            className='cursor-pointer'
          >
            <Checkbox.Control className='cursor-pointer'>
              <Checkbox.Indicator />
            </Checkbox.Control>
            <Checkbox.Label className='cursor-pointer'>
              Meio de pagamento diferente nas demais parcelas?
            </Checkbox.Label>
          </Checkbox.Root>

          {!allowDifferentPayment && (
            <Text className='text-xs text-alba-gray-600'>
              O produto selecionado não permite meios de pagamento diferentes.
            </Text>
          )}
        </div>
      )}
    </>
  );
}

type PaymentDifferentPaymentFieldsProps = {
  state: PaymentState;
  errors: PaymentErrors;
  isViewMode: boolean;
  isOtherDebitPayment: boolean;
  bankMaskConfig: BankMaskConfig | null;
  paymentMethodOptions: FormSelectOption[];
  onChange: PaymentStateChangeHandler;
  onSelectChange: PaymentSelectChangeHandler;
};

function PaymentDifferentPaymentFields({
  state,
  errors,
  isViewMode,
  isOtherDebitPayment,
  bankMaskConfig,
  paymentMethodOptions,
  onChange,
  onSelectChange,
}: PaymentDifferentPaymentFieldsProps) {
  const agencyMaxLength = bankMaskConfig?.agencyLength ?? 5;
  const agencyPlaceholder =
    bankMaskConfig?.agencyPlaceholder ?? 'Número da agência';
  const agencyDigitEnabled = bankMaskConfig
    ? bankMaskConfig.agencyDigitEnabled
    : true;
  const agencyDigitMaxLength = bankMaskConfig?.agencyDigitLength ?? 1;
  const agencyDigitPlaceholder = bankMaskConfig
    ? bankMaskConfig.agencyDigitEnabled
      ? bankMaskConfig.agencyDigitPlaceholder
      : 'Não se aplica'
    : 'Dígito da agência';
  const accountMaxLength = bankMaskConfig?.accountLength ?? 13;
  const accountPlaceholder =
    bankMaskConfig?.accountPlaceholder ?? 'Número da conta';
  const accountDigitMaxLength = bankMaskConfig?.accountDigitLength ?? 2;
  const accountDigitPlaceholder =
    bankMaskConfig?.accountDigitPlaceholder ?? 'Dígito de controle';

  return (
    <div className='grid grid-cols-1 gap-6 lg:grid-cols-2'>
      <FormSelect
        label='Meio de Pagamento'
        placeholder='Selecione o meio de pagamento'
        required
        options={paymentMethodOptions}
        value={state.otherPaymentMethod}
        onChange={value => onSelectChange('otherPaymentMethod', value)}
        error={errors.otherPaymentMethod}
        disabled={isViewMode}
      />
      {/* Mesma regra das demais parcelas: dados bancários só no débito. */}
      {isOtherDebitPayment && (
        <>
          <div className='hidden lg:block' />
          <FormInput
            label='Agência'
            placeholder={agencyPlaceholder}
            required
            value={state.otherAgency}
            onChange={value => onChange('otherAgency', value)}
            error={errors.otherAgency}
            disabled={isViewMode}
            type='text'
            inputMode='numeric'
            pattern={`[0-9]{1,${agencyMaxLength}}`}
            maxLength={agencyMaxLength}
          />
          <FormInput
            label='Dígito da Agência'
            placeholder={agencyDigitPlaceholder}
            required={false}
            value={state.otherAgencyDigit}
            onChange={value => onChange('otherAgencyDigit', value)}
            error={errors.otherAgencyDigit}
            disabled={isViewMode || !agencyDigitEnabled}
            type='text'
            maxLength={agencyDigitMaxLength}
          />
          <FormInput
            label='Conta'
            placeholder={accountPlaceholder}
            required
            value={state.otherAccount}
            onChange={value => onChange('otherAccount', value)}
            error={errors.otherAccount}
            disabled={isViewMode}
            type='text'
            inputMode='numeric'
            pattern={`[0-9]{1,${accountMaxLength}}`}
            maxLength={accountMaxLength}
          />
          <FormInput
            label='Dígito da Conta'
            placeholder={accountDigitPlaceholder}
            required
            value={state.otherAccountDigit}
            onChange={value => onChange('otherAccountDigit', value)}
            error={errors.otherAccountDigit}
            disabled={isViewMode}
            type='text'
            maxLength={accountDigitMaxLength}
          />
        </>
      )}
    </div>
  );
}

type PaymentDueDateFieldsProps = {
  state: PaymentState;
  errors: PaymentErrors;
  isViewMode: boolean;
  /** Cartão de crédito: todas as parcelas no mesmo dia. */
  allowDifferentDueDate: boolean;
  firstDueDateMin: string;
  firstDueDateMax: string;
  onChange: PaymentStateChangeHandler;
};

function PaymentDueDateFields({
  state,
  errors,
  isViewMode,
  allowDifferentDueDate,
  firstDueDateMin,
  firstDueDateMax,
  onChange,
}: PaymentDueDateFieldsProps) {
  const showDifferentDueDate = allowDifferentDueDate && state.differentDueDate;

  return (
    <div className='flex flex-col gap-4'>
      <div className='grid grid-cols-1 gap-6 lg:grid-cols-2'>
        <RequiredFieldTooltip active>
          <Calendar
            label={
              <>
                Vencimento da Primeira Parcela
                <span className='text-alba-error-red-500'>*</span>
              </>
            }
            value={
              state.firstDueDate
                ? [DateTime.fromISO(state.firstDueDate).toJSDate()]
                : undefined
            }
            onValueChange={value =>
              onChange(
                'firstDueDate',
                value.map(date => date.toISOString().split('T')[0]).join(',')
              )
            }
            disabled={isViewMode}
            min={
              firstDueDateMin
                ? DateTime.fromISO(firstDueDateMin).toJSDate()
                : undefined
            }
            max={
              firstDueDateMax
                ? DateTime.fromISO(firstDueDateMax).toJSDate()
                : undefined
            }
            isDateUnavailable={date => {
              const dayOfWeek = date.toDate('UTC').getDay();
              return dayOfWeek === 5 || dayOfWeek === 6;
            }}
          >
            {errors.firstDueDate && (
              <p className='text-alba-error-red-500 text-xs font-montserrat font-normal leading-tight'>
                {errors.firstDueDate}
              </p>
            )}
          </Calendar>
        </RequiredFieldTooltip>
      </div>

      {allowDifferentDueDate &&
        state.installment &&
        Number(state.installment) > 1 && (
          <Checkbox.Root
            checked={state.differentDueDate}
            disabled={isViewMode}
            onCheckedChange={details => {
              if (isViewMode) return;
              const raw =
                typeof details === 'boolean' || typeof details === 'string'
                  ? details
                  : details?.checked;
              const checked = raw === 'indeterminate' ? false : !!raw;
              onChange('differentDueDate', checked);
            }}
            onClick={() => {
              if (isViewMode) return;
              const current = state.differentDueDate || false;
              onChange('differentDueDate', !current);
            }}
            className='cursor-pointer'
          >
            <Checkbox.Control className='cursor-pointer'>
              <Checkbox.Indicator />
            </Checkbox.Control>
            <Checkbox.Label className='cursor-pointer'>
              Vencimento diferente nas demais parcelas.
            </Checkbox.Label>
          </Checkbox.Root>
        )}

      {showDifferentDueDate && (
        <div className='grid grid-cols-1 gap-6 lg:grid-cols-2'>
          <FormInput
            label='Dia de Vencimento das Demais Parcelas'
            placeholder='Ex: 15'
            required
            value={state.otherDueDate}
            onChange={value => onChange('otherDueDate', value)}
            error={errors.otherDueDate}
            disabled={isViewMode}
            type='text'
            inputMode='numeric'
            maxLength={2}
          />
        </div>
      )}
      {showDifferentDueDate && !errors.otherDueDate && (
        <Text className='text-xs text-alba-gray-600'>
          O dia informado será o vencimento das demais parcelas a partir do mês
          seguinte à primeira parcela.
        </Text>
      )}
    </div>
  );
}

type PaymentActionsProps = {
  onBack: () => void;
  onNext: () => void;
  isViewMode: boolean;
  isLoading?: boolean;
};

function PaymentActions({
  onBack,
  onNext,
  isViewMode,
  isLoading = false,
}: PaymentActionsProps) {
  return (
    <div className='flex flex-row items-center justify-between gap-3 self-stretch px-4 sm:gap-6 sm:px-6'>
      <Button
        type='button'
        variant='ghost'
        size='md'
        className='flex h-12 min-w-0 items-center justify-start gap-2 px-0 text-alba-blue-500 hover:text-alba-blue-600 sm:h-auto'
        onClick={onBack}
      >
        <ArrowLeft className='h-5 w-5' strokeWidth={1.6} />
        Voltar
      </Button>
      <Button
        variant='primary'
        size='md'
        className='flex h-12 min-w-[132px] items-center justify-center gap-2 rounded-xl px-5 py-2 sm:h-auto sm:min-w-[148px] sm:px-6'
        onClick={onNext}
        disabled={isViewMode || isLoading}
        loading={isLoading}
      >
        {isLoading ? 'Processando...' : 'Próximo'}
        {!isLoading && <ArrowRight className='h-5 w-5' strokeWidth={1.9} />}
      </Button>
    </div>
  );
}
